import {Skeleton, Stack, Tooltip, useTheme, type Theme} from '@mui/material';
import Box from '@mui/material/Box';

import useParseURL from 'hooks/useParseURL';

import {URLParams, type ActionProcessed, type HarvestRecommendationProcessed} from 'interfaces';

import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import {grey} from '@mui/material/colors';
import {SWR_IMMUTABLE_OPTIONS as localOptions} from 'appConstants';
import {type CageMortality, type GrowthPerCage, type LiceStatus} from 'client';
import {numberDisplay} from 'helpers/UnitsFormatHandler';
import {useGetCageGrowthForecast, useGetCageMoralityForecast, useGetLice} from 'hooks/swr';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {type ReactChild} from 'react';
import {useTranslation} from 'react-i18next';
import {normalizeColor} from 'utils/colorUtils';
import {ActionChip} from './ActionChip';
import {GrowthErrorTag} from './GrowthErrorTag';
import {useShortTermViewState} from './shortTermViewState';

const findCageData = <T extends {cageId: string}>(cageDataArray: T[], cageId: string) => {
  const value = cageDataArray.find((element) => element.cageId === cageId);
  if (value === undefined) {
    return null;
  }
  return value;
};

const HarvestCardTags = ({
  openRightMenu,
  harvest,
  siteId,
  isRecommendation,
}: {
  openRightMenu?: () => void;
  harvest: ActionProcessed | HarvestRecommendationProcessed;
  siteId: string;
  isRecommendation: boolean;
}) => {
  const {shortTermStatusV2Enabled} = useFlags();
  const {planId} = useParseURL();
  const {t} = useTranslation('common');
  const {tags} = useShortTermViewState();

  const displayGrowth = tags.includes(URLParams.growthTag) || isRecommendation;
  const displayLice = tags.includes(URLParams.liceTag) || isRecommendation;
  const displayMortality = tags.includes(URLParams.mortalityTag) || isRecommendation;

  const theme = useTheme();

  const {data: growthByCage} = useGetCageGrowthForecast({resourceId: displayGrowth ? planId : undefined, localOptions});
  const {data: mortalityByCage} = useGetCageMoralityForecast({
    resourceId: displayMortality ? planId : undefined,
    localOptions,
  });
  const liceResourceId = displayLice ? [harvest.date, siteId, harvest.cageId] : undefined;
  const {data: actionLice} = useGetLice({
    resourceId: liceResourceId,
    localOptions,
  });

  const cageMortality = mortalityByCage
    ? findCageData(mortalityByCage?.cageMortality ?? [], harvest.cageId)
    : undefined;
  const cageMortalityColor = cageMortality
    ? cageMortalityColorHandler(cageMortality, theme)
    : theme.palette.warning.light;
  const liceDataProcessed = actionLice && liceDataHandler(theme, actionLice);
  const liceTooltip = `${t('avgAdultFemaleLice')}: ${actionLice?.avgAdultFemaleLice} \n  ${t(
    'avgMobileLice',
  )}: ${actionLice?.avgMobileLice} \n  ${t('avgStationaryLice')}: ${actionLice?.avgStationaryLice} \n  ${t(
    'source',
  )}: ${actionLice?.source}`;
  const cardColor = cardColorsHandler(theme, harvest.cageId, growthByCage);
  const shortTermGrowthModelStatus = cardColor?.shortTermGrowthModelStatus;
  const hasGrowthError = cardColor ? !cardColor.hasShortTermGrowthModel : null;
  const tooltipTitle = t('poorGrowthModelData');
  return (
    <Stack onClick={openRightMenu} direction={'row'} alignItems="center" marginTop={-0.5}>
      {/* TODO: remove this line and ErrorTag after shortTermStatusV2Enabled deployed  */}
      {!shortTermStatusV2Enabled && displayGrowth && hasGrowthError && <ErrorTag tooltipTitle={tooltipTitle} />}

      {shortTermStatusV2Enabled && displayGrowth && shortTermGrowthModelStatus && (
        <GrowthErrorTag
          shortTermGrowthModelStatus={shortTermGrowthModelStatus}
          boxProps={{marginRight: 0.5, marginBottom: 0.5}}
        />
      )}

      {displayGrowth && cardColor !== null && (
        <CardTag
          {...{
            title: t('growth').charAt(0).toUpperCase(),
            tooltipTitle: `${t('dailygrowthRateDescription')}`,
            value: cardColor ? cardColor.cageGrowthRate : undefined,
            backgroundColor: cardColor ? cardColor.backgroundColor : undefined,
          }}
        />
      )}
      {displayMortality && cageMortality !== null && (
        <CardTag
          {...{
            title: t('mortality').charAt(0).toUpperCase(),
            value: cageMortality && `${numberDisplay(cageMortality.mortalityRate * 1000, 2)}‰`,
            backgroundColor: cageMortalityColor,
            tooltipTitle:
              cageMortality && `${t('dailyMortalityRate')}: ${numberDisplay(cageMortality.mortalityRate * 1000, 4)}`,
          }}
        />
      )}
      {displayLice && (
        <CardTag
          {...{
            title: t('lice').charAt(0).toUpperCase(),
            value: liceDataProcessed ? liceDataProcessed?.value : undefined,
            backgroundColor: liceDataProcessed?.color,
            tooltipTitle: liceTooltip,
          }}
        />
      )}
    </Stack>
  );
};

export const CardTag = ({
  title,
  value,
  backgroundColor,
  tooltipTitle,
  notUppercase,
}: {
  title: string;
  value?: string | number | null;
  backgroundColor?: string;
  tooltipTitle?: string;
  notUppercase?: boolean;
}) => {
  const _tooltipTitle = tooltipTitle ? <div style={{whiteSpace: 'pre-line'}}>{tooltipTitle}</div> : '';

  if (value === null) {
    return null;
  }

  return (
    <Tooltip title={_tooltipTitle} placement="right" arrow>
      <span>
        {value !== undefined ? (
          <ActionChip {...{title, value, backgroundColor, tooltipTitle, notUppercase}} />
        ) : (
          <Skeleton width={50} height={18} sx={{marginRight: 0.5, marginTop: 0.5}} />
        )}
      </span>
    </Tooltip>
  );
};

const ErrorTag = ({tooltipTitle}: {tooltipTitle?: string | ReactChild}) => {
  const theme = useTheme();
  return (
    <Tooltip title={tooltipTitle ?? ''} placement="right" arrow sx={{whiteSpace: 'pre-line'}}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          borderRadius: 100,
          textTransform: 'uppercase',
          marginRight: 0.5,
          marginBottom: 0.5,
          color: grey[900],
          backgroundColor: theme.palette.error.light,
        }}
      >
        <PriorityHighIcon
          sx={{
            height: '18px',
            width: '100%',
            padding: '2px',
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default HarvestCardTags;

const getLiceRetioColor: (aboveLiceLimit: boolean, theme: Theme) => string = (aboveLiceLimit, theme) => {
  return aboveLiceLimit ? theme.palette.bad.main : theme.palette.good.main;
};

const cageMortalityColorHandler = (cageMortality: CageMortality, theme: Theme) => {
  const {mortalityRate} = cageMortality;
  if (mortalityRate < 0.00025) {
    return theme.palette.good.main;
  } else if (mortalityRate < 0.0005) {
    return theme.palette.neutral.main;
  } else {
    return theme.palette.bad.main;
  }
};

const liceDataHandler: (theme: Theme, actionLice: LiceStatus) => {color: string; value: null | string} = (
  theme,
  actionLice,
) => {
  const {aboveLiceLimit, avgAdultFemaleLice} = actionLice;
  const liceValue = avgAdultFemaleLice ?? null;
  const liceColor = getLiceRetioColor(aboveLiceLimit, theme);
  return {color: liceColor, value: liceValue !== null ? `${numberDisplay(liceValue, 2)}` : liceValue};
};

const cardColorsHandler = (theme: Theme, cageId: string, growthByCage?: GrowthPerCage) => {
  if (!growthByCage) {
    return undefined;
  }
  const cageGrowth = growthByCage?.cageGrowth?.find((growth) => growth.cageId === cageId);
  if (!cageGrowth) {
    return null;
  }
  const cageGrowthRate = cageGrowth?.growthRate
    ? numberDisplay(Math.round(cageGrowth?.growthRate * 10000) / 100, 2)
    : undefined;
  const cageRelativeGrowthRate = cageGrowth?.relativeGrowthRate ? cageGrowth?.relativeGrowthRate : 0;
  const hasShortTermGrowthModel = cageGrowth?.hasShortTermGrowthModel;
  const shortTermGrowthModelStatus = cageGrowth.shortTermGrowthModelStatus;
  const backgroundColor = normalizeColor(
    cageRelativeGrowthRate,
    -0.15,
    0.15,
    theme.palette.bad.main,
    theme.palette.neutral.main,
    theme.palette.good.main,
  );
  const fontColor = theme.palette.text.secondary;
  return {backgroundColor, fontColor, hasShortTermGrowthModel, cageGrowthRate, shortTermGrowthModelStatus};
};
